/**
 * Восстановление пароля (#1342): тексты ошибок и таймер повторной отправки.
 *
 * Чистые функции без React — их проверяют юнит-тесты (`__tests__/resetPassword.test.ts`).
 */

/** Повторная отправка ссылки — не раньше чем через 60 с, как код в TwoFactorForm. */
export const RESET_RESEND_COOLDOWN_SECONDS = 60;

/** Верхняя граница пароля — та же, что у сервера (`ResetPasswordBody.password`, 6..200). */
export const RESET_PASSWORD_MAX_LENGTH = 200;

export const RESET_ERRORS = {
    /** Существующий текст локальной проверки формы — не меняется. */
    email: "Введите корректный email",
    password: "Пароль от 6 до 200 символов",
    link: "Ссылка недействительна или истекла",
    generic: "Проверьте введённые данные и попробуйте ещё раз",
} as const;

/** Текст успеха: одинаковый для любого адреса — сервер не говорит, есть ли аккаунт. */
export const RESET_SENT_TEXT = {
    title: "Проверьте почту",
    body: "Если аккаунт с этим адресом есть, мы отправили на него ссылку для сброса пароля. Загляните и в папку «Спам».",
    socialHint: "Входили через Telegram, VK или Яндекс? Используйте ту же кнопку на экране входа.",
    toast: "Если аккаунт с этим адресом есть, мы отправили ссылку для сброса пароля",
    resent: "Ссылка отправлена ещё раз",
} as const;

type ValidationItem = {loc?: unknown};

/** Ответ 422 сервера: `error` — JSON-список ошибок pydantic, `code` — VALIDATION_ERROR. */
function parseValidationItems(error: string): ValidationItem[] | null {
    const t = error.trim();
    if (!t.startsWith("[") && !t.startsWith("{")) return null;
    try {
        const parsed: unknown = JSON.parse(t);
        if (Array.isArray(parsed)) return parsed as ValidationItem[];
        return parsed && typeof parsed === "object" ? [parsed as ValidationItem] : [];
    } catch {
        return [];
    }
}

function validationItems(error: string, code?: string | null): ValidationItem[] | null {
    const items = parseValidationItems(error);
    if (items !== null) return items;
    return code === "VALIDATION_ERROR" ? [] : null;
}

function fieldsOf(items: ValidationItem[]): Set<string> {
    const fields = new Set<string>();
    for (const item of items) {
        if (Array.isArray(item?.loc)) {
            for (const part of item.loc) if (typeof part === "string") fields.add(part);
        }
    }
    return fields;
}

/** Ошибка `/forgot-password` для человека: сырой JSON 422 → «Введите корректный email». */
export function forgotPasswordErrorText(error: string, code?: string | null): string {
    const items = validationItems(error, code);
    if (items === null) return error;
    return RESET_ERRORS.email;
}

/** Ошибка `/reset-password` для человека: 422 по полю пароля или токена — русским текстом. */
export function resetPasswordErrorText(error: string, code?: string | null): string {
    const items = validationItems(error, code);
    if (items === null) return error;
    const fields = fieldsOf(items);
    if (fields.has("password")) return RESET_ERRORS.password;
    if (fields.has("token")) return RESET_ERRORS.link;
    return RESET_ERRORS.generic;
}

/** Сколько секунд осталось до «Отправить ещё раз». Считаем от момента отправки — таймер не «уплывает» в фоне. */
export function resendSecondsLeft(sentAtMs: number, nowMs: number, cooldown = RESET_RESEND_COOLDOWN_SECONDS): number {
    const elapsed = Math.floor(Math.max(0, nowMs - sentAtMs) / 1000);
    return Math.max(0, cooldown - elapsed);
}

/** «00:59» — тот же формат, что у таймера кода в TwoFactorForm. */
export function formatResendCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
